import React, { useState, useCallback, useMemo, useRef, useEffect } from "react";
import InfiniteScrollList from "../../../components/dataview/List";
import EstateCard, { EstateCardSkeleton } from "../../../components/card/Sale";
import FilterChipBar from "../components/FilterChipBar";
import ExploreBottomBar from "../components/ExploreBottomBar";
import FullFilter from "../components/FullFilter";
import SingleFilterModal from "../components/SingleFilterModal";
import { buildFilterSections } from "../constants/filterSections";
import { ORDER_OPTIONS } from "../constants/filterOptions";
import legacy, { clear_land_polygons } from "../../../utils/legacy";
import icons from "../../../assets/images/icons";
import styles from "./DesktopLayout.module.scss";

/**
 * PC 데스크톱 레이아웃
 *
 * 피그마: 좌측 리스트 패널(396px) + 우측 맵(나머지)
 * sideview(aside-section)는 listPanel 기준 left:100%로 오른쪽에 자동 배치됨 (legacy CSS)
 */
const DesktopLayout = ({ filter, dataHook, mapHook, sideviewRef, mapInteractionRef, markerSelectRef, onSearch, searchOverlay }) => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [singleFilterId, setSingleFilterId] = useState(null);
  const [chipDropdownLeft, setChipDropdownLeft] = useState(0);
  const mapTopBarRef = useRef(null);
  const [sortOrder, setSortOrder] = useState(ORDER_OPTIONS[0].value);

  // 지도 조작(드래그/줌/클릭) 및 마커 클릭 시 필터 팝업 닫기
  const closeFilters = useCallback(() => {
    setIsFilterOpen(false);
    setSingleFilterId(null);
  }, []);

  useEffect(() => {
    if (mapInteractionRef) mapInteractionRef.current = closeFilters;
    if (markerSelectRef) markerSelectRef.current = closeFilters;
    return () => {
      if (mapInteractionRef) mapInteractionRef.current = null;
      if (markerSelectRef) markerSelectRef.current = null;
    };
  }, [mapInteractionRef, markerSelectRef, closeFilters]);

  const { payload, handlePayload, handleResetPayload } = filter;

  const filterSections = useMemo(
    () => buildFilterSections({ selectedCategory: payload.selectedCategory || null }),
    [payload.selectedCategory]
  );

  const {
    data,
    detailData,
    isLoading,
    hasMore,
    listRef,
    loadMore,
    fetchDetailList,
    requestLoadData,
  } = dataHook;
  const { isMove, setIsMove, mapCount, zoomRequired, drawPolygonForEstate } = mapHook;

  // ── 필터 ──

  const handleFilterApply = useCallback(() => {
    setIsFilterOpen(false);
    setIsMove(true);
  }, [setIsMove]);

  // ── 상세페이지 열기 ──

  // suid로 상세페이지 열기 + show 시 폴리곤 그리기
  const openSaleWithPolygon = useCallback(
    (suid, item) => {
      if (!suid) return;
      legacy.open_sale_to_sideview(sideviewRef.current, suid, {
        move: true,
        handler: {
          show: () => {
            if (item) drawPolygonForEstate(item);
          },
          close: () => {
            clear_land_polygons();
          },
        },
      });
    },
    [drawPolygonForEstate, sideviewRef]
  );

  // 펼친 상태에서 개별 매물 클릭 (pnu 정보 없을 수 있음)
  const openSaleDetail = useCallback(
    (suid) => openSaleWithPolygon(suid, null),
    [openSaleWithPolygon]
  );

  // 리스트 카드 클릭
  const handleCardClick = useCallback(
    (item) => {
      const suid = item.suid || item.list?.[0]?.suid;
      if (!suid) return;
      openSaleWithPolygon(suid, item);
    },
    [openSaleWithPolygon]
  );

  // 리스트 호버 시 지도에 마커 표시
  const hoverMarkerRef = useRef(null);
  const handleHover = useCallback((lat, lng) => {
    if (hoverMarkerRef.current) {
      hoverMarkerRef.current.setMap(null);
      hoverMarkerRef.current = null;
    }
    if (lat && lng) {
      hoverMarkerRef.current = new legacy.daum.maps.Marker({
        position: new legacy.daum.maps.LatLng(lat, lng),
        map: legacy.map,
        zIndex: 1000,
      });
    }
  }, []);
  const handleLeave = useCallback(() => {
    if (hoverMarkerRef.current) {
      hoverMarkerRef.current.setMap(null);
      hoverMarkerRef.current = null;
    }
  }, []);

  const handleClose = useCallback(() => {
    legacy.bh?.go_before?.();
  }, []);

  const handleSearch = useCallback(() => {
    onSearch?.();
  }, [onSearch]);

  const handleRefresh = useCallback(() => {
    requestLoadData();
    setIsMove(false);
  }, [requestLoadData, setIsMove]);

  return (
    <div className={styles.root} ref={sideviewRef}>
      {/* 좌측 리스트 패널 */}
      <div className={styles.listPanel}>
        <div className={styles.panelHeader}>
          <button type="button" className={styles.backBtn} onClick={handleClose}>
            <ChevronLeftIcon />
          </button>
          <div className={styles.headerTitle}>
            <span className={styles.logo}>disco</span>
            <span className={styles.logoSub}>매물</span>
          </div>
          <button type="button" className={styles.searchBtn} onClick={handleSearch}>
            <SearchIcon />
          </button>
        </div>

        <div className={styles.subHeader}>
          <span className={styles.totalCount}>
            총 <strong>{data.totalCount ?? 0}개</strong> 매물
          </span>
          <select
            className={styles.sortSelect}
            value={JSON.stringify(sortOrder)}
            onChange={(e) => {
              const newSort = JSON.parse(e.target.value);
              setSortOrder(newSort);
              // 정렬 변경 시 리스트 리로드
              dataHook.loadData({
                ...payload,
                sort_type: newSort.type,
                sort_order: newSort.order,
              }, 0);
            }}
          >
            {ORDER_OPTIONS.map((opt) => (
              <option key={opt.label} value={JSON.stringify(opt.value)}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.listBody}>
          {zoomRequired && data.list.length === 0 && !isLoading ? (
            <div className={styles.emptyState}>
              <img src={icons.empty.list} alt="" className={styles.emptyImage} />
              <p className={styles.emptyText}>
                지도를 더 확대해주세요
              </p>
            </div>
          ) : data.list.length === 0 && !isLoading ? (
            <div className={styles.emptyState}>
              <img src={icons.empty.list} alt="" className={styles.emptyImage} />
              <p className={styles.emptyText}>
                조건에 맞는 매물이 없어요
                <br />
                지도를 이동하거나 필터를 변경해보세요.
              </p>
            </div>
          ) : (
            <InfiniteScrollList
              ref={listRef}
              items={data.list}
              renderItem={(d) => (
                <EstateCard
                  {...d}
                  list={detailData[d.groupKey]?.data || []}
                  onClick={() => handleCardClick(d)}
                  onOpenDetail={openSaleDetail}
                  onMoreClick={() => fetchDetailList(d.groupKey)}
                  onHover={handleHover}
                  onLeave={handleLeave}
                  initialExpanded={data.autoExpand}
                />
              )}
              onTrigger={loadMore}
              isLoading={isLoading}
              hasMore={hasMore}
              renderSkeleton={() => <EstateCardSkeleton />}
              skeletonCount={3}
            />
          )}
        </div>

        {/* 검색 오버레이 — listPanel 내부에 absolute로 표시 */}
        {searchOverlay}
      </div>

      {/* 우측 맵 영역 */}
      <div className={styles.mapPanel}>
        <div className={styles.mapTopBar} ref={mapTopBarRef}>
          <FilterChipBar
            payload={payload}
            onChange={handlePayload}
            onFullFilter={() => {
              setSingleFilterId(null);
              setIsFilterOpen(true);
            }}
            onChipClick={(chipId, e) => {
              const chipEl = e.currentTarget;
              const barEl = mapTopBarRef.current;
              if (barEl) {
                const barRect = barEl.getBoundingClientRect();
                const chipRect = chipEl.getBoundingClientRect();
                let left = chipRect.left - barRect.left;
                const margin = 12;
                const dropdownWidth = 375;
                const maxLeft = barRect.width - dropdownWidth - margin;
                if (left > maxLeft) left = Math.max(margin, maxLeft);
                setChipDropdownLeft(left);
              }
              setIsFilterOpen(false);
              setSingleFilterId(chipId);
            }}
            onReset={() => { handleResetPayload(); setSingleFilterId(null); }}
          />
          {singleFilterId && (
            <div className={styles.chipDropdown} style={{ left: chipDropdownLeft }}>
              <SingleFilterModal
                filterId={singleFilterId}
                filter={payload}
                onChange={handlePayload}
                onClose={() => setSingleFilterId(null)}
                selectedCategory={payload.selectedCategory}
              />
            </div>
          )}
        </div>

        <ExploreBottomBar
          count={mapCount}
          onClick={handleRefresh}
          zoomRequired={zoomRequired}
          visible={isMove}
        />

        {isFilterOpen && (
          <div className={styles.filterPopup}>
            <FullFilter
              sections={filterSections}
              filter={payload}
              onChange={handlePayload}
              onReset={handleResetPayload}
              onApply={handleFilterApply}
              onClose={() => setIsFilterOpen(false)}
              resultCount={data.totalCount}
            />
          </div>
        )}

      </div>
    </div>
  );
};

const ChevronLeftIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SearchIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M21 21l-4.35-4.35M19 11a8 8 0 11-16 0 8 8 0 0116 0z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default DesktopLayout;
